import { useEffect, useState } from "react";
import { errorMessage } from "../../../api/client";
import { templateApi } from "../../../api/templates";
import type { BulkCreateResponse, ShiftTemplate, TemplatePreview, TemplateRowStatus, TemplateRunBody } from "../../../api/types";
import { Modal } from "../../../components/Overlay";
import { Button, cn, ErrorText } from "../../../components/ui";
import { BulkResults } from "../shifts/BulkResults";
import { EmployeePicker } from "../shifts/EmployeePicker";
import { NotifyCrewToggle } from "../shifts/CrewResponse";
import { DateChips, RecurringFields } from "./RunDates";

export const ROW_LABEL: Record<TemplateRowStatus, string> = {
  WILL_CREATE: "Will create", CONFLICT_OVERLAP: "Conflict: overlap", DUPLICATE: "Duplicate same venue/date",
  NEEDS_ATTENTION: "Needs Attention", EMPLOYEE_INACTIVE: "Employee inactive", VENUE_INACTIVE: "Venue inactive",
};
const TONE: Record<TemplateRowStatus, string> = {
  WILL_CREATE: "text-emerald-400", NEEDS_ATTENTION: "text-warn", CONFLICT_OVERLAP: "text-signal",
  DUPLICATE: "text-signal", EMPLOYEE_INACTIVE: "text-zinc-400", VENUE_INACTIVE: "text-zinc-400",
};
const sel = "h-11 w-full rounded-sm border border-white/15 bg-surface px-3 text-sm text-white";

function PreviewTable({ p }: { p: TemplatePreview }) {
  return (
    <div data-testid="run-preview">
      <div className="mb-3 flex flex-wrap gap-3 text-xs">
        <span data-testid="run-preview-total" className="text-white">{p.total} rows</span>
        {Object.entries(p.counts).map(([k, n]) => <span key={k} data-testid={`run-preview-count-${k}`} className={TONE[k as TemplateRowStatus]}>{ROW_LABEL[k as TemplateRowStatus]}: {n}</span>)}
      </div>
      <ul className="max-h-72 divide-y divide-white/5 overflow-y-auto rounded-sm border border-white/10">
        {p.rows.map((r) => (
          <li key={`${r.date}-${r.employee_id}`} data-testid={`run-preview-row-${r.date}-${r.employee_id}`} className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 px-4 py-2 text-sm sm:grid-cols-[auto_1fr_auto]">
            <span className="font-mono text-xs text-zinc-400">{r.date}</span>
            <span className="order-last col-span-2 min-w-0 truncate text-white sm:order-none sm:col-span-1">{r.employee_name ?? "Unknown"}</span>
            <span className={cn("label-caps justify-self-end", TONE[r.status])} data-testid={`run-preview-status-${r.date}-${r.employee_id}`}>{ROW_LABEL[r.status]}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TemplateSelect({ value, onChange }: { value: ShiftTemplate | null; onChange: (t: ShiftTemplate | null) => void }) {
  const [list, setList] = useState<ShiftTemplate[]>([]);
  useEffect(() => { templateApi.list().then(setList).catch(() => setList([])); }, []);
  return (
    <select className={sel} aria-label="Template" value={value?.id ?? ""} onChange={(e) => onChange(list.find((t) => t.id === e.target.value) ?? null)} data-testid="run-template-select">
      <option value="">Select a template…</option>
      {list.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  );
}

export function TemplateRunModal({ open, template, onClose, onDone, onOpenShift }: {
  open: boolean; template: ShiftTemplate | null; onClose: () => void; onDone: () => void; onOpenShift: (id: string) => void;
}) {
  const [tpl, setTpl] = useState<ShiftTemplate | null>(template);
  const [body, setBody] = useState<TemplateRunBody>({ mode: "ONCE", employee_ids: [] });
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [result, setResult] = useState<BulkCreateResponse | null>(null);
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTpl(template); setPreview(null); setResult(null); setError(null);
    setBody({ mode: "ONCE", employee_ids: template?.default_employee_ids ?? [], dates: [], weekdays: [], skip_dates: [] });
  }, [open, template]);
  const set = (p: Partial<TemplateRunBody>) => { setBody((b) => ({ ...b, ...p })); setPreview(null); };
  const pick = (t: ShiftTemplate | null) => { setTpl(t); set({ employee_ids: t?.default_employee_ids ?? [] }); };

  const run = async (confirm: boolean) => {
    if (!tpl) return;
    if (!body.employee_ids.length) { setError("Select at least one employee."); return; }
    setBusy(true); setError(null);
    try {
      if (!confirm) { setPreview(await templateApi.preview(tpl.id, body)); setKey(crypto.randomUUID()); }
      else { setResult(await templateApi.createShifts(tpl.id, { ...body, idempotency_key: key })); onDone(); }
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };
  const creatable = preview ? (preview.counts.WILL_CREATE ?? 0) + (preview.counts.NEEDS_ATTENTION ?? 0) : 0;

  return (
    <Modal open={open} onClose={onClose} title={tpl ? `Create shifts · ${tpl.name}` : "Create shifts from template"} testId="template-run-modal" wide>
      {result ? <BulkResults result={result} onOpen={onOpenShift} onDone={onClose} /> : (
        <div className="space-y-5">
          {!template && <TemplateSelect value={tpl} onChange={pick} />}
          <div className="inline-flex rounded-full border border-white/15 p-1" role="tablist">
            {(["ONCE", "RECURRING"] as const).map((m) => (
              <button key={m} type="button" role="tab" aria-selected={body.mode === m} onClick={() => set({ mode: m })}
                className={cn("rounded-full px-4 py-1.5 text-xs uppercase tracking-wider", body.mode === m ? "bg-signal text-white" : "text-zinc-400 hover:text-white")}
                data-testid={`run-mode-${m.toLowerCase()}`}>{m === "ONCE" ? "One-time" : "Recurring"}</button>
            ))}
          </div>
          {body.mode === "ONCE" ? <DateChips label="Dates" dates={body.dates ?? []} onChange={(d) => set({ dates: d })} testId="run-dates" /> : <RecurringFields body={body} set={set} />}
          <div><p className="label-caps mb-2 text-zinc-400">Employees</p><EmployeePicker selected={body.employee_ids} onChange={(ids) => set({ employee_ids: ids })} /></div>
          <NotifyCrewToggle checked={!!body.notify_crew} onChange={(v) => setBody((b) => ({ ...b, notify_crew: v }))} testId="run-notify-crew-checkbox"
            hint="Sends one summary text per employee for this run, not one per shift." />
          <ErrorText message={error} testId="run-error" />
          {preview && <PreviewTable p={preview} />}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="run-cancel">Cancel</Button>
            <Button type="button" variant="secondary" disabled={busy || !tpl} onClick={() => run(false)} data-testid="run-preview-button">Preview</Button>
            <Button type="button" disabled={busy || !preview || creatable === 0} onClick={() => run(true)} data-testid="run-confirm-button">{busy ? "Working…" : `Create ${creatable} shift${creatable === 1 ? "" : "s"}`}</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
